import React, { useState, useEffect, useCallback } from 'react';
import { Snackbar } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import DomesticAirline, {
  type DomesticAirlineResponseArgs,
  convertDomesticAirlineResponseToArgs
} from '@this/domain/god/domestic_airline';
import { Loading } from '@this/shared/ui/feedbacks/loading/loading';
import { Modal, ModalHeader, ModalBody } from '@this/shared/ui/feedbacks/modal/modal';
import { Fetcher } from '@this/src/util';
import { ConfirmModal } from '@this/components/shared/ui/feedbacks/modal/confirm_modal';
import { Button } from '@this/shared/ui/inputs/button';
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@this/shared/ui/data_displays/table';
import { Input } from '@this/shared/ui/inputs/input';
import DomesticAirlineForm from './domestic_airline_form';
import { formatDate } from './utils';

function getStyles() {
  return {
    searchInput: {
      marginRight: '10px',
      marginLeft: '6px',
      fontSize: '13px',
      width: '150px'
    } as React.CSSProperties,
    inputAreaRight: {
      display: 'flex',
      marginBottom: '20px',
      alignItems: 'center'
    } as React.CSSProperties,
    error: {
      color: '#dc3545',
      marginBottom: '20px'
    } as React.CSSProperties,
    actionButtonRow: {
      display: 'flex',
      gap: '8px'
    } as React.CSSProperties
  } as const;
}

interface DomesticAirlinesResponse {
  domestic_airlines: DomesticAirlineResponseArgs[];
}

const DomesticAirlinesTab: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [domesticAirlines, setDomesticAirlines] = useState<DomesticAirline[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchThreeLetterCode, setSearchThreeLetterCode] = useState<string>('');
  const [searchTwoLetterCode, setSearchTwoLetterCode] = useState<string>('');
  const [searchName, setSearchName] = useState<string>('');
  const [debouncedThreeLetterCode, setDebouncedThreeLetterCode] = useState<string>('');
  const [debouncedTwoLetterCode, setDebouncedTwoLetterCode] = useState<string>('');
  const [debouncedName, setDebouncedName] = useState<string>('');
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [newDomesticAirline, setNewDomesticAirline] = useState<DomesticAirline | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<DomesticAirline | null>(null);
  const [editDomesticAirline, setEditDomesticAirline] = useState<DomesticAirline | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<DomesticAirline | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedThreeLetterCode(searchThreeLetterCode);
      setDebouncedTwoLetterCode(searchTwoLetterCode);
      setDebouncedName(searchName);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchThreeLetterCode, searchTwoLetterCode, searchName]);

  const fetchDomesticAirlines = useCallback(async () => {
    setLoading(true);
    const params: {
      three_letter_code?: string;
      two_letter_code?: string;
      name?: string;
    } = {};
    if (debouncedThreeLetterCode) params.three_letter_code = debouncedThreeLetterCode;
    if (debouncedTwoLetterCode) params.two_letter_code = debouncedTwoLetterCode;
    if (debouncedName) params.name = debouncedName;
    return Fetcher.get<DomesticAirlinesResponse>('/god/domestic_airlines', params).then(
      response => {
        const airlines = response.domestic_airlines.map(
          data => new DomesticAirline(convertDomesticAirlineResponseToArgs(data))
        );
        setLoading(false);
        setDomesticAirlines(airlines);
        setError(null);
      },
      () => {
        setLoading(false);
        setError('通信環境が不安定です。\n時間をおいてもう一度お試しください。');
      }
    );
  }, [debouncedThreeLetterCode, debouncedTwoLetterCode, debouncedName]);

  useEffect(() => {
    fetchDomesticAirlines();
  }, [fetchDomesticAirlines]);

  const openAddModal = useCallback(() => {
    setShowAddModal(true);
    setNewDomesticAirline(DomesticAirline.createEmpty());
    setAddError(null);
  }, []);

  const closeAddModal = useCallback(() => {
    setShowAddModal(false);
    setNewDomesticAirline(null);
    setAddError(null);
  }, []);

  const handleAddDomesticAirline = useCallback(async () => {
    if (!newDomesticAirline) return;
    if (!newDomesticAirline.threeLetterCode) {
      setAddError('3レターコードは必須です');
      return;
    }
    if (!newDomesticAirline.name) {
      setAddError('航空会社名は必須です');
      return;
    }
    setLoading(true);
    setAddError(null);
    try {
      await Fetcher.post('/god/domestic_airlines', newDomesticAirline.submitParams);
      const addedName = newDomesticAirline.name;
      closeAddModal();
      fetchDomesticAirlines();
      setSnackbarMessage(`${addedName}を追加しました`);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string[] } } };
      setAddError(err.response?.data?.error?.join('\n') || '登録に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [newDomesticAirline, closeAddModal, fetchDomesticAirlines]);

  const openEditModal = useCallback((domesticAirline: DomesticAirline) => {
    setShowEditModal(true);
    setEditTarget(domesticAirline);
    setEditDomesticAirline(
      new DomesticAirline({
        id: domesticAirline.id,
        threeLetterCode: domesticAirline.threeLetterCode,
        twoLetterCode: domesticAirline.twoLetterCode,
        name: domesticAirline.name,
        createdAt: domesticAirline.createdAt,
        updatedAt: domesticAirline.updatedAt
      })
    );
    setEditError(null);
  }, []);

  const closeEditModal = useCallback(() => {
    setShowEditModal(false);
    setEditTarget(null);
    setEditDomesticAirline(null);
    setEditError(null);
  }, []);

  const handleEditDomesticAirlineSubmit = useCallback(async () => {
    if (!editTarget || !editDomesticAirline) return;
    if (!editDomesticAirline.threeLetterCode) {
      setEditError('3レターコードは必須です');
      return;
    }
    if (!editDomesticAirline.name) {
      setEditError('航空会社名は必須です');
      return;
    }
    setLoading(true);
    setEditError(null);
    try {
      await Fetcher.put(`/god/domestic_airlines/${editTarget.id}`, editDomesticAirline.submitParams);
      const updatedName = editDomesticAirline.name;
      closeEditModal();
      fetchDomesticAirlines();
      setSnackbarMessage(`${updatedName}を更新しました`);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string[] } } };
      setEditError(err.response?.data?.error?.join('\n') || '更新に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [editTarget, editDomesticAirline, closeEditModal, fetchDomesticAirlines]);

  const openDeleteModal = useCallback((domesticAirline: DomesticAirline) => {
    setShowDeleteModal(true);
    setDeleteTarget(domesticAirline);
    setDeleteError(null);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
    setDeleteError(null);
  }, []);

  const handleDeleteDomesticAirline = useCallback(async () => {
    if (!deleteTarget) return;
    setLoading(true);
    setDeleteError(null);
    try {
      const deletedName = deleteTarget.name;
      await Fetcher.delete(`/god/domestic_airlines/${deleteTarget.id}`);
      closeDeleteModal();
      fetchDomesticAirlines();
      setSnackbarMessage(`${deletedName}を削除しました`);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string[] } } };
      setDeleteError(err.response?.data?.error?.join('\n') || '削除に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [deleteTarget, closeDeleteModal, fetchDomesticAirlines]);

  const styles = getStyles();

  return (
    <div>
      <div style={styles.inputAreaRight}>
        <span>3レターコード:</span>
        <Input
          style={styles.searchInput}
          value={searchThreeLetterCode}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchThreeLetterCode(e.target.value)}
        />
        <span>2レターコード:</span>
        <Input
          style={styles.searchInput}
          value={searchTwoLetterCode}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTwoLetterCode(e.target.value)}
        />
        <span>航空会社名:</span>
        <Input
          style={styles.searchInput}
          value={searchName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchName(e.target.value)}
        />
        <Button size="small" onClick={openAddModal} style={{ marginLeft: 'auto' }}>
          新規追加
        </Button>
      </div>
      <div>
        {error && <div style={{ ...styles.error, whiteSpace: 'pre-wrap' }}>{error}</div>}
        <div style={{ height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {loading && <Loading size="small" />}
        </div>
        <div style={{ opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
          <TableContainer height="60vh">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width="80px">ID</TableCell>
                  <TableCell width="150px">3レターコード</TableCell>
                  <TableCell width="150px">2レターコード</TableCell>
                  <TableCell width="240px">航空会社名</TableCell>
                  <TableCell width="180px">作成日時</TableCell>
                  <TableCell width="180px">更新日時</TableCell>
                  <TableCell width="150px">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {domesticAirlines.length === 0 ? (
                  <TableRow>
                    <TableCell style={{ textAlign: 'center' }} colSpan={7}>
                      データがありません
                    </TableCell>
                  </TableRow>
                ) : (
                  domesticAirlines.map(airline => (
                    <TableRow key={airline.id}>
                      <TableCell>{airline.id}</TableCell>
                      <TableCell>{airline.threeLetterCode}</TableCell>
                      <TableCell>{airline.twoLetterCode || '-'}</TableCell>
                      <TableCell>{airline.name}</TableCell>
                      <TableCell>{formatDate(airline.createdAt)}</TableCell>
                      <TableCell>{formatDate(airline.updatedAt)}</TableCell>
                      <TableCell>
                        <div style={styles.actionButtonRow}>
                          <Button size="small" onClick={() => openEditModal(airline)}>
                            編集
                          </Button>
                          <Button size="small" color="danger" onClick={() => openDeleteModal(airline)}>
                            削除
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
      {newDomesticAirline && (
        <Modal open={showAddModal} onClose={() => closeAddModal()}>
          <ModalHeader>国内航空会社 新規追加</ModalHeader>
          <ModalBody>
            <DomesticAirlineForm
              domesticAirline={newDomesticAirline}
              onSubmit={handleAddDomesticAirline}
              onCancel={closeAddModal}
              error={addError}
              submitLabel="登録"
              cancelLabel="キャンセル"
            />
          </ModalBody>
        </Modal>
      )}
      {editDomesticAirline && (
        <Modal open={showEditModal} onClose={() => closeEditModal()}>
          <ModalHeader>国内航空会社 編集</ModalHeader>
          <ModalBody>
            <DomesticAirlineForm
              domesticAirline={editDomesticAirline}
              onSubmit={handleEditDomesticAirlineSubmit}
              onCancel={closeEditModal}
              error={editError}
              submitLabel="更新"
              cancelLabel="キャンセル"
              note="利用者向けの参照APIや国内線の表示も変更されます。"
            />
          </ModalBody>
        </Modal>
      )}
      <ConfirmModal
        open={showDeleteModal}
        onCancel={closeDeleteModal}
        onSubmit={handleDeleteDomesticAirline}
        description={
          deleteTarget
            ? deleteError
              ? [deleteError]
              : [
                  '本当に削除しますか？',
                  `3レターコード: ${deleteTarget.threeLetterCode}`,
                  `航空会社名: ${deleteTarget.name}`
                ]
            : ''
        }
        color="danger"
        submitButtonLabel="削除"
      />
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert onClose={() => setSnackbarMessage(null)} severity="success">
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default DomesticAirlinesTab;
